import React from 'react';
import { Link } from 'react-router-dom';
import { Flame, Wind, Wrench, Fan, ArrowRight, Phone, Calendar, Check } from 'lucide-react';

const ServicesPage = () => {
  return (
    <main>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl mt-4 mb-6">
              Residential & Commercial HVAC Services
            </h1>
            <p className="text-xl text-stone-300 mb-8">
              While other HVAC companies focus on plumbing, electrical, even roofing - we pride ourselves on being experts at two things: Heating & Cooling your home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="services-estimate-btn">
                <Calendar size={20} />
                Get Free Estimate
              </Link>
              <a href="tel:8592550728" className="btn-secondary inline-flex items-center justify-center gap-2 bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900" data-testid="services-call-btn">
                <Phone size={20} />
                (859) 255-0728
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-32 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Heating */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-xl transition-all duration-300" data-testid="service-detail-heating">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-red-600"></div>
              <div className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mb-6">
                  <Flame className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-4">Heating Services</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">Keep your home warm and cozy with our professional heating services. From furnace repair to complete system replacement.</p>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Furnace Repair</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Furnace Replacement</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Furnace Maintenance</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Heat Pump Services</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Boiler Repair</li>
                </ul>
                <Link to="/services/heating" className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-3 transition-all" data-testid="learn-more-heating">
                  Learn More <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* Cooling */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-xl transition-all duration-300" data-testid="service-detail-cooling">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-cyan-600"></div>
              <div className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mb-6">
                  <Wind className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-4">Cooling Services</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">Stay cool all summer long with our expert air conditioning services. Fast repairs and efficient installations.</p>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />AC Repair</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />AC Replacement</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />AC Maintenance</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Ductless Mini-Splits</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Central Air Systems</li>
                </ul>
                <Link to="/services/cooling" className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-3 transition-all" data-testid="learn-more-cooling">
                  Learn More <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* Installation */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-xl transition-all duration-300" data-testid="service-detail-installation">
              <div className="h-2 bg-gradient-to-r from-emerald-500 to-teal-600"></div>
              <div className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6">
                  <Wrench className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-4">HVAC Installation</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">Professional installation services for new HVAC systems. We help you choose the right system for your home and budget.</p>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />New System Installation</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />System Upgrades</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Ductwork Installation</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Smart Thermostat Setup</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Zoning Systems</li>
                </ul>
                <Link to="/services/installation" className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-3 transition-all" data-testid="learn-more-installation">
                  Learn More <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* Air Quality */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-xl transition-all duration-300" data-testid="service-detail-air-quality">
              <div className="h-2 bg-gradient-to-r from-purple-500 to-indigo-600"></div>
              <div className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-6">
                  <Fan className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-4">Air Quality</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">Breathe easier with our indoor air quality solutions. We help eliminate allergens, pollutants, and improve ventilation.</p>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Air Filtration</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />UV Air Purifiers</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Humidity Control</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Duct Cleaning</li>
                  <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Ventilation Solutions</li>
                </ul>
                <Link to="/services/air-quality" className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-3 transition-all" data-testid="learn-more-air-quality">
                  Learn More <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Contact us today for a free estimate. Our certified technicians are ready to help with all your HVAC needs.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300" data-testid="services-cta-btn">
            Get Your Free Estimate <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
